import { Layout, Skeleton, theme } from "antd";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./layout/sidebar";
import { Wrapper } from "./style";
import { TableLoading } from "../components/table-animation";
import { BorderFilter } from "../config";
import { Admins } from "../routers/admin";
import { Users } from "../routers/user";
import { BookACallModalUI } from "../components/book-a-call/BookACallModal";

const { Content } = Layout;

const Page = (props) => {
  const { user, setData } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const [reloadPage, setReloadPage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [bookCallOpen, setBookCallOpen] = useState(false);
  const [bookCallLoading, setBookCallLoading] = useState(true);
  const [bookCallPopupOpen, setBookCallPopupOpen] = useState(() => window.innerWidth >= 768);

  const handleLogout = () => {
    setData({});
    navigate("/login");
  };

  useEffect(() => {
    if (Object.keys(user)?.length === 0) {
      handleLogout();
    }

    setLoading(false);

    const handleStorage = (e) => {
      if (e?.storageArea?.length === 0) {
        setData({});
        return navigate("/login");
      }

      if (user) {
        const menusName = (
            !Object.keys(user)?.includes("admin") ? Admins : Users
        )?.map((d) => `/${d?.path}`);

        navigate(
            menusName?.includes(`${location.pathname}`)
                ? window.location.pathname
                : `${menusName?.[0]}`
        );
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [user]);

  // --- Subscription / Trial Banner ---
  const BANNER_HEIGHT = 40;
  const ud = user?.user?.user_data || {};
  const hasTrial         = !!ud.has_trial;
  const remainingTrialDays = ud.remaining_trial_days ?? 0;
  const isTrialExpired   = !!ud.is_trial_expired;
  const hasSubscription  = !!ud.has_subscription;
  const subEndsAt        = ud.subscription?.ends_at; // unix seconds

  let bannerMsg = null;
  let bannerBg  = '#314F71'; // default navy

  if (hasTrial && !isTrialExpired && !hasSubscription) {
    bannerMsg = `${remainingTrialDays} days left on your trial. Subscribe now, keep going.`;
  } else if (hasSubscription && subEndsAt) {
    // Subscription ending within 10 days
    const daysLeft = Math.ceil((subEndsAt * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 10 && daysLeft > 0) {
      bannerBg  = '#314F71';
      bannerMsg = daysLeft === 1
        ? `Your subscription expires tomorrow. Renew to keep access.`
        : `Your subscription expires in ${daysLeft} days. Renew to keep access.`;
    }
  }

  // --- Logic for Breakpoints & Attributes ---
  useEffect(() => {
    const breakpoint = 992;
    document.body.setAttribute("data-kt-app-header-enabled", "false");
    document.body.setAttribute("data-kt-app-header-fixed", "false");

    const syncAttributes = () => {
      if (window.innerWidth < breakpoint) {
        document.body.setAttribute("data-kt-app-sidebar-minimize", "off");
        document.body.setAttribute(
            "data-kt-drawer",
            isSidebarMinimized ? "on" : "off"
        );
      } else {
        document.body.setAttribute(
            "data-kt-app-sidebar-minimize",
            isSidebarMinimized ? "on" : "off"
        );
        document.body.setAttribute("data-kt-drawer", "off");
      }
    };

    syncAttributes();
    window.addEventListener("resize", syncAttributes);
    return () => window.removeEventListener("resize", syncAttributes);
  }, [isSidebarMinimized]);

  useEffect(() => {
    const sidebar = document.getElementById("kt_app_sidebar");
    const breakpoint = 992;

    const applySidebarClasses = () => {
      if (window.innerWidth < breakpoint) {
        sidebar?.classList.add("drawer", "drawer-start");
      } else {
        sidebar?.classList.remove("drawer", "drawer-start", "drawer-on");
        const overlay = document.getElementById("drawer-overlay");
        overlay?.remove();
      }
    };

    applySidebarClasses();
    window.addEventListener("resize", applySidebarClasses);
    return () => window.removeEventListener("resize", applySidebarClasses);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarMinimized((prev) => {
      const newState = !prev;
      const sidebar = document.getElementById("kt_app_sidebar");
      const overlayId = "drawer-overlay";
      const breakpoint = 992;

      if (window.innerWidth < breakpoint) {
        // Drawer logic for mobile < 992px
        let overlay = document.getElementById(overlayId);
        if (newState) {
          sidebar?.classList.add("drawer-on");
          if (!overlay) {
            overlay = document.createElement("div");
            overlay.id = overlayId;
            overlay.className = "drawer-overlay";
            overlay.style.cssText =
                `z-index:105; position:fixed; top:${bannerMsg ? BANNER_HEIGHT : 0}px; left:0; width:100%; height:100%; background:rgba(0,0,0,0.5); transition: opacity 0.3s ease;`;
            document.body.appendChild(overlay);
            overlay.addEventListener("click", () => {
              setIsSidebarMinimized(false);
              sidebar?.classList.remove("drawer-on");
              overlay.remove();
            });
          }
        } else {
          sidebar?.classList.remove("drawer-on");
          overlay?.remove();
        }
      } else {
        // Minimize logic for desktop >= 992px
        if (newState) {
          sidebar?.classList.add("sidebar-minimized");
        } else {
          sidebar?.classList.remove("sidebar-minimized");
        }
      }
      return newState;
    });
  };

  const { email, name } = user?.user?.user_data || {};

  // Calendly URL with prefilled user fields
  const calendlyBase = 'https://calendly.com/himanshu-adsify/sellerview-agency-demo-clone';
  const calendlyParams = new URLSearchParams();
  if (name) calendlyParams.set('name', name);
  if (email) calendlyParams.set('email', email);
  const calendlyQuery = calendlyParams.toString();
  const calendlySrc = calendlyQuery ? `${calendlyBase}?${calendlyQuery}` : calendlyBase;

  return (
      <div
          className="app-page flex-column flex-column-fluid"
          id="kt_app_page"
          style={{ backgroundColor: "#F1F2F4" }}
      >
        <style>{`
          @media (max-width: 991px) {
            .mobile-fab {
              display: flex !important;
              position: fixed;
              bottom: 24px;
              right: 24px;
              z-index: 200;
              width: 44px;
              height: 44px;
              border-radius: 50%;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 16px rgba(0,0,0,0.18);
            }
          }
          @media (min-width: 992px) {
            .mobile-fab {
              display: none !important;
            }
          }
        `}</style>

        {bannerMsg && (
          <div
            role="status"
            aria-live="polite"
            className="subscription-banner d-flex align-items-center justify-content-center gap-3"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 106,
              height: BANNER_HEIGHT,
              background: bannerBg,
              color: '#ffffff',
              padding: '8px 12px',
              fontWeight: 600,
              fontSize: '14px',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            {bannerMsg}
            {!hasSubscription && (
              <a
                href="/settings/billing"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#fff',
                  color: '#314F71',
                  fontWeight: 700,
                  fontSize: 13,
                  padding: '4px 14px',
                  borderRadius: 20,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  transition: 'background 0.15s, color 0.15s',
                  flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                Choose a Plan →
              </a>
            )}
          </div>
        )}

        <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">

          <Sidebar
              id="kt_app_sidebar"
              user={user}
              setUser={setData}
              setData={setData}
              type={user?.loginType}
              collapsed={isSidebarMinimized}
              location={location}
              handleLogout={handleLogout}
              toggleSidebar={toggleSidebar}
              bannerHeight={bannerMsg ? BANNER_HEIGHT : 0}
          />

          <div className="app-main flex-column flex-row-fluid" id="kt_app_main"
               style={{ paddingTop: bannerMsg ? BANNER_HEIGHT : 0 }}>
            <div className="d-flex flex-column flex-column-fluid">
              <div id="kt_app_content">
                <div id="kt_app_content_container" className="app-container container-fluid">
                  {reloadPage ? (
                      <Wrapper>
                        <Outlet />
                      </Wrapper>
                  ) : (
                      <>
                        <div
                            className="card mb-3 mt-4 px-3"
                            style={{ border: BorderFilter }}
                        >
                          <div className="card-header d-flex justify-content-between align-items-center">
                            <div className="card-title d-flex align-items-center">
                              <Skeleton.Input active />
                            </div>
                            <div className="card-toolbar">
                              <Skeleton.Button active />
                            </div>
                          </div>
                        </div>

                        <div className="card">
                          <div style={{ padding: "0px 15px", marginTop: "10px" }}>
                            <TableLoading
                                id="test-table"
                                columns={[1, 2, 3, 4, 5]}
                                checkBoxVal={true}
                                actions={[1, 2]}
                            />
                          </div>
                        </div>
                      </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom-right FAB — mobile only, outside content flow so it never overlaps page buttons */}
        <button
            onClick={toggleSidebar}
            className="btn btn-danger mobile-fab"
            style={{ display: "none" }}
            title="Toggle Sidebar"
        >
          <i className="ki-outline ki-abstract-14 fs-2 pe-0"></i>
        </button>

        {/* ─── Book a Demo — chat widget ───────────────────────────────── */}
        <BookACallModalUI
            open={bookCallOpen}
            onClose={() => setBookCallOpen(false)}
            loading={bookCallLoading}
            onLoad={() => setBookCallLoading(false)}
            calendlySrc={calendlySrc}
        />

        <>
          <style>{`
            /* ── Floating avatar circle ── */
            .sv-chat-fab {
              position: fixed;
              bottom: 24px;
              right: 24px;
              z-index: 1050;
              width: 60px;
              height: 60px;
              border-radius: 50%;
              cursor: pointer;
              overflow: visible;
              box-shadow: 0 4px 20px rgba(49,79,113,0.4);
              transition: transform 0.2s ease, box-shadow 0.2s ease;
              flex-shrink: 0;
            }
            .sv-chat-fab:hover {
              transform: scale(1.07);
              box-shadow: 0 6px 28px rgba(49,79,113,0.55);
            }
            .sv-chat-fab__img {
              width: 60px;
              height: 60px;
              border-radius: 50%;
              object-fit: cover;
              border: 3px solid #fff;
              display: block;
            }
            /* Pulse ring */
            .sv-chat-fab__ring {
              position: absolute;
              inset: -6px;
              border-radius: 50%;
              border: 2.5px solid rgba(49,79,113,0.4);
              animation: svChatPulse 2.4s ease-out infinite;
              pointer-events: none;
            }
            @keyframes svChatPulse {
              0%   { transform: scale(0.92); opacity: 0.9; }
              70%  { transform: scale(1.22); opacity: 0; }
              100% { transform: scale(0.92); opacity: 0; }
            }

            /* ── Speech-bubble popup ── */
            .sv-chat-popup {
              position: fixed;
              bottom: 98px;
              right: 24px;
              z-index: 1051;
              width: 292px;
              background: #fff;
              border-radius: 16px;
              box-shadow: 0 8px 40px rgba(0,0,0,0.16);
              animation: svPopupIn 0.2s cubic-bezier(.4,0,.2,1);
              overflow: hidden;
            }
            /* Tail pointing at the circle */
            .sv-chat-popup::after {
              content: '';
              position: absolute;
              bottom: -9px;
              right: 26px;
              border-left: 9px solid transparent;
              border-right: 9px solid transparent;
              border-top: 9px solid #fff;
              filter: drop-shadow(0 2px 2px rgba(0,0,0,0.06));
            }
            @keyframes svPopupIn {
              from { opacity: 0; transform: translateY(10px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0)    scale(1);    }
            }
            .sv-chat-popup__head {
              background: linear-gradient(135deg, #314F71 0%, #4A6FA5 100%);
              padding: 14px 16px;
              display: flex;
              align-items: center;
              gap: 10px;
              position: relative;
            }
            .sv-chat-popup__head-avatar {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              object-fit: cover;
              border: 2px solid rgba(255,255,255,0.35);
              flex-shrink: 0;
            }
            .sv-chat-popup__head-title {
              color: #fff;
              font-size: 14px;
              font-weight: 700;
              line-height: 1.3;
              font-family: inherit;
            }
            .sv-chat-popup__head-sub {
              color: rgba(255,255,255,0.72);
              font-size: 11px;
              margin-top: 2px;
              font-family: inherit;
            }
            .sv-chat-popup__x {
              position: absolute;
              top: 9px;
              right: 9px;
              background: rgba(255,255,255,0.15);
              border: none;
              color: #fff;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              font-size: 15px;
              line-height: 1;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: background 0.2s;
              font-family: inherit;
            }
            .sv-chat-popup__x:hover { background: rgba(255,255,255,0.28); }
            .sv-chat-popup__body {
              padding: 16px 16px 18px;
            }
            .sv-chat-popup__msg {
              font-size: 13.5px;
              color: #333;
              line-height: 1.6;
              margin: 0 0 14px;
              font-family: inherit;
            }
            .sv-chat-popup__cta {
              width: 100%;
              background: linear-gradient(135deg, #314F71 0%, #4A6FA5 100%);
              color: #fff;
              border: none;
              border-radius: 10px;
              padding: 11px 0;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
              transition: opacity 0.2s, transform 0.15s;
              font-family: inherit;
              letter-spacing: 0.1px;
            }
            .sv-chat-popup__cta:hover {
              opacity: 0.9;
              transform: translateY(-1px);
            }
            /* Mobile: shift above the sidebar toggle */
            @media (max-width: 991px) {
              .sv-chat-fab   { bottom: 80px; }
              .sv-chat-popup { bottom: 154px; }
            }
          `}</style>

          {/* Speech-bubble popup */}
          {bookCallPopupOpen && (
            <div className="sv-chat-popup">
              <div className="sv-chat-popup__head">
                <img
                  src="/assets/media/avatars/SalesPerson.png"
                  alt="P&L Expert"
                  className="sv-chat-popup__head-avatar"
                />
                <div>
                  <div className="sv-chat-popup__head-title">Talk to our P&amp;L Expert</div>
                </div>
                <button
                  className="sv-chat-popup__x"
                  onClick={() => setBookCallPopupOpen(false)}
                  type="button"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="sv-chat-popup__body">
                <p className="sv-chat-popup__msg">
                  Let us find the P&L leaks for you on a call - <strong>LIVE</strong>
                </p>
                <button
                  className="sv-chat-popup__cta"
                  type="button"
                  onClick={() => {
                    setBookCallPopupOpen(false);
                    setBookCallLoading(true);
                    setBookCallOpen(true);
                  }}
                >
                  Book a Free Call →
                </button>
              </div>
            </div>
          )}

          {/* Floating avatar circle */}
          <div
            className="sv-chat-fab"
            role="button"
            aria-label="Talk to P&L Expert"
            onClick={() => setBookCallPopupOpen((v) => !v)}
          >
            <span className="sv-chat-fab__ring" />
            <img
              src="/assets/media/avatars/SalesPerson.png"
              alt="P&L Expert"
              className="sv-chat-fab__img"
            />
          </div>
        </>

        {/* ─── Trial-expired full-page lock ───────────────────────────── */}
        {isTrialExpired && !hasSubscription && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            backgroundColor: 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              textAlign: 'center',
              padding: '48px 40px',
              borderRadius: 16,
              background: '#fff',
              boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
              maxWidth: 460,
              width: '90%',
            }}>
              <i className="ki-outline ki-lock-2" style={{ fontSize: 52, color: '#ccc', display: 'block', marginBottom: 16 }} />
              <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: '#1a1a2e' }}>
                Your Trial Has Expired
              </h3>
              <p style={{ color: '#888', fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
                Your free trial has ended. Upgrade to a paid plan to continue using SellerView and access all features.
              </p>
              <button
                className="btn btn-danger"
                onClick={() => navigate('/settings/billing')}
                style={{ borderRadius: 8, fontWeight: 600, padding: '10px 32px', fontSize: 15 }}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        )}

      </div>
  );
};

export default Page;